import { type EmailAddress } from '../useEmlParser';

/**
 * Formats an email address or array of addresses for display.
 * Single address: "John Doe <john@example.com>" or "john@example.com"
 * Multiple addresses: "John <john@example.com>, jane@example.com"
 */
export const formatEmailAddress = (address: EmailAddress | EmailAddress[] | null | undefined): string => {
  if (!address) return '';

  if (Array.isArray(address)) {
    return address.map((a) => (a.name ? `${a.name} <${a.email}>` : a.email)).join(', ');
  }

  return address.name ? `${address.name} <${address.email}>` : address.email;
};

/**
 * Clean noise from email text body.
 * Removes HTML artifacts, thread history, excessive whitespace, and other noise.
 *
 * Based on the clean_email_body Python function from srv-ergeon/scripts/clean_email_bodies.py
 *
 * @param text - Original email text body
 * @returns Cleaned text body
 */
export const cleanEmailBody = (text: string | null | undefined): string => {
  if (!text) {
    return text || '';
  }

  let cleanedText = text;

  // Decode common HTML entities (but mark lines that only have these)
  // First, identify lines that are ONLY these entities and spaces
  const linesOnlyWithEntities = cleanedText.split('\n').map(line => {
    // Check if line only contains entity patterns and whitespace
    const entityOnlyPattern = /^(\s|&zwnj;|&zwj;|&#8203;|&#8202;|&shy;|&nbsp;|;)+$/;
    return entityOnlyPattern.test(line);
  });

  // Now decode entities
  const htmlEntities: Record<string, string> = {
    '&nbsp;': ' ',
    '&zwnj;': '', // Zero-width non-joiner
    '&zwj;': '', // Zero-width joiner
    '&#8203;': '', // Zero-width space
    '&#8202;': '', // Hair space
    '&shy;': '', // Soft hyphen
    '&lt;': '<',
    '&gt;': '>',
    '&amp;': '&',
    '&quot;': '"',
    '&#39;': "'",
  };

  Object.entries(htmlEntities).forEach(([entity, replacement]) => {
    const regex = new RegExp(entity, 'g');
    cleanedText = cleanedText.replace(regex, replacement);
  });

  // Only remove lines that were entity-only lines (now empty after decoding)
  cleanedText = cleanedText.split('\n').filter((line, index) => {
    if (linesOnlyWithEntities[index]) {
      // This was an entity-only line, remove it if now empty
      return line.trim().length > 0;
    }
    // Keep all other lines (including intentionally empty paragraph breaks)
    return true;
  }).join('\n');

  // Remove HTML content (but preserve URLs in angle brackets)
  // First, preserve URLs in angle brackets by replacing them temporarily
  const urlsInBrackets: string[] = [];
  cleanedText = cleanedText.replace(/<(https?:\/\/[^>]+)>/g, (match, url) => {
    const placeholder = `__URL_PLACEHOLDER_${urlsInBrackets.length}__`;
    urlsInBrackets.push(url);
    return placeholder;
  });

  // Remove entire style and script blocks (including their content)
  cleanedText = cleanedText.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
  cleanedText = cleanedText.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');

  // Remove remaining HTML tags (div, table, etc.)
  cleanedText = cleanedText.replace(/<[^>]+>/g, '');

  // Remove incomplete HTML tags (text truncated in the middle of a tag)
  // Matches any < followed by characters but no closing >
  cleanedText = cleanedText.replace(/<[^>]*$/g, '');

  // Restore the URLs
  urlsInBrackets.forEach((url, index) => {
    const placeholder = `__URL_PLACEHOLDER_${index}__`;
    cleanedText = cleanedText.replace(placeholder, url);
  });

  // Merge URL continuation lines (query parameters on separate lines)
  // URLs are often wrapped across multiple lines or separated by spaces: the second+ lines start with &
  // Remove trailing spaces before the line break, and also spaces before & in general (malformed URLs)
  cleanedText = cleanedText.replace(/\s*\r?\n\s*&/g, '&');

  // Also fix inline spaces before & in URL query strings (e.g., "param=value &param2=value2")
  cleanedText = cleanedText.replace(/([=\w])\s+&/g, '$1&');

  // Remove asset/tracking URLs (fonts, images, tracking pixels)
  // This must happen before removing parentheses, otherwise URLs won't be on their own line
  const assetDomains = [
    'fonts\\.googleapis\\.com',
    'fonts\\.gstatic\\.com',
    'www\\.facebook\\.com/tr',
    'track\\.',
    'pixel\\.',
    'analytics\\.',
  ];

  for (const domain of assetDomains) {
    // Remove URLs with these domains (with optional parentheses) on their own line
    const lineRegex = new RegExp(`^\\s*\\(?\\s*https?:\\/\\/[^\\s\\)]*${domain}[^\\s\\)]*\\s*\\)?\\s*$`, 'gm');
    cleanedText = cleanedText.replace(lineRegex, '');

    // Also remove inline URLs with these domains (with optional parentheses)
    const inlineRegex = new RegExp(`\\(?\\s*https?:\\/\\/[^\\s\\)]*${domain}[^\\s\\)]*\\s*\\)?`, 'g');
    cleanedText = cleanedText.replace(inlineRegex, '');
  }

  // Remove parentheses around remaining URLs but keep the URL itself
  // Handles patterns like "Read in Browser ( \n https://example.com \n )"
  // Common in converted HTML emails
  cleanedText = cleanedText.replace(/\(\s*(https?:\/\/[^\s\)]+)\s*\)/g, ' $1');

  // Remove phone number artifacts like (tel:650-300-4854)
  cleanedText = cleanedText.replace(/\(tel:[^\)]+\)/g, '');

  // Remove common email thread indicators and everything after
  // Patterns like "On Jan 1, 2024, at 10:00 AM, John wrote:"
  const threadPatterns = [
    /\n\s*On\s+.+?wrote:.*$/is,
    /\n\s*From:.*$/is,
    /\n\s*Sent:.*$/is,
    /\n\s*-+\s*Original Message\s*-+.*$/is,
    /\n\s*_{5,}.*$/is, // Long underscores separator
    /\n\s*-{5,}.*$/is, // Long dashes separator
  ];

  for (const pattern of threadPatterns) {
    cleanedText = cleanedText.replace(pattern, '');
  }

  // Remove quoted lines (lines starting with >)
  cleanedText = cleanedText.replace(/^\s*>.*$/gm, '');

  // Remove common footer patterns
  const footerPatterns = [
    /©\s*\d{4}\s+.*?(?:Inc|LLC|Ltd).*?(?:Privacy Policy|Contact Support|Unsubscribe)/is,
    /\bPrivacy Policy\b.*?$/is,
    /\bUnsubscribe\b.*?$/is,
    /\bContact Support\b.*?$/is,
    /This email was sent to.*$/is,
    /To stop receiving.*$/is,
    /If you.*no longer wish to receive.*$/is,
  ];

  for (const pattern of footerPatterns) {
    cleanedText = cleanedText.replace(pattern, '');
  }

  // Remove common signature separators
  cleanedText = cleanedText.replace(/\n\s*--\s*$/gm, '');

  // Remove invisible/zero-width characters commonly used in HTML emails for spacing
  // Using separate replaces to avoid misleading-character-class lint error
  cleanedText = cleanedText.replace(/\u034F/g, ''); // Combining Grapheme Joiner
  cleanedText = cleanedText.replace(/\u00AD/g, ''); // Soft Hyphen
  cleanedText = cleanedText.replace(/\u200B/g, ''); // Zero-width space
  cleanedText = cleanedText.replace(/\u200C/g, ''); // Zero-width non-joiner
  cleanedText = cleanedText.replace(/\u200D/g, ''); // Zero-width joiner
  cleanedText = cleanedText.replace(/\uFEFF/g, ''); // Zero-width no-break space (BOM)

  // Remove lines that are just whitespace or symbols
  cleanedText = cleanedText.replace(/^\s*[•\-_|]+\s*$/gm, '');

  // Remove lines that are only whitespace
  cleanedText = cleanedText.replace(/^\s*$/gm, '');

  // Remove excessive newlines (more than 2 consecutive)
  cleanedText = cleanedText.replace(/\n{3,}/g, '\n\n');

  // Collapse multiple consecutive spaces into single space
  cleanedText = cleanedText.replace(/ {2,}/g, ' ');

  // Remove leading/trailing whitespace from each line
  const lines = cleanedText.split('\n').map((line) => line.trim());
  cleanedText = lines.join('\n');

  // Remove empty lines at start and end
  cleanedText = cleanedText.trim();

  // Final pass - if we have more than 2 consecutive empty lines, reduce to 2
  cleanedText = cleanedText.replace(/\n\n+/g, '\n\n');

  return cleanedText;
};
